import React, { useState, useEffect } from 'react';
import UnsavedChangesGuard from '../components/UnsavedChangesGuard';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

import { rayonnagesAPI, bibliotequesAPI } from '../services/api';

function ModifierRayonnagePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;
  const bibliothequeIdFromUrl = searchParams.get('bibliotheque_id');

  // État pour détecter les modifications non sauvegardées
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { control, handleSubmit, formState: { errors, isDirty }, reset, watch, setValue, getValues } = useForm({
    defaultValues: {
      nom: '',
      bibliotheque_id: '',
      description: '',
      position_x: 0,
      position_y: 0
    }
  });

  // Récupérer les données du rayonnage si en mode édition
  const { data: rayonnage, isLoading, error } = useQuery(
    ['rayonnage', id],
    () => rayonnagesAPI.getById(id).then(res => res.data.data),
    {
      enabled: isEditMode
    }
  );

  // Requête pour récupérer les bibliothèques
  const { data: bibliotheques } = useQuery(
    'bibliotheques',
    () => bibliotequesAPI.getAll().then(res => res.data.data),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // Surveiller les modifications du formulaire
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
  }, [isDirty]);

  // Pré-remplir le formulaire quand les données sont disponibles
  useEffect(() => {
    if (rayonnage) {
      reset({
        nom: rayonnage.nom || '',
        bibliotheque_id: rayonnage.bibliotheque_id || '',
        description: rayonnage.description || '',
        position_x: rayonnage.position_x || 0,
        position_y: rayonnage.position_y || 0
      });
    } else if (!isEditMode) {
      reset({
        nom: '',
        bibliotheque_id: bibliothequeIdFromUrl || '',
        description: '',
        position_x: 0,
        position_y: 0
      });
    }
  }, [rayonnage, reset, isEditMode, bibliothequeIdFromUrl]);

  // Mutation pour créer un rayonnage
  const createRayonnageMutation = useMutation(
    (data) => {
      // Nettoyer les champs vides et convertir les positions en nombres
      const cleanData = {
        nom: data.nom,
        bibliotheque_id: data.bibliotheque_id,
        description: data.description || '',
        position_x: parseInt(data.position_x) || 0,
        position_y: parseInt(data.position_y) || 0
      };
      return rayonnagesAPI.create(cleanData);
    },
    {
      onSuccess: (response, variables) => {
        toast.success(t('modifierRayonnagePage.createSuccess'));
        const bibliothequeId = variables.bibliotheque_id;
        queryClient.invalidateQueries('rayonnages');
        queryClient.invalidateQueries('rayonnages-list');
        // Invalider les données de la bibliothèque pour mettre à jour le nombre de rayonnages
        if (bibliothequeId) {
          queryClient.invalidateQueries(['bibliotheque', bibliothequeId]);
          queryClient.invalidateQueries(['bibliotheque-rayonnages', bibliothequeId]);
        }
        navigate(-1);
      },
      onError: (error) => {
        console.error('Erreur lors de la création:', error);
        
        if (error.response?.status === 409) {
          toast.error(t('modifierRayonnagePage.alreadyExists'));
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          errors.forEach(err => {
            if (err.message) {
              toast.error(err.message);
            }
          });
        } else {
          toast.error(t('modifierRayonnagePage.createError'));
        }
      }
    }
  );

  // Mutation pour mettre à jour le rayonnage
  const updateRayonnageMutation = useMutation(
    (data) => {
      // Nettoyer les champs vides et convertir les positions en nombres
      const cleanData = {
        nom: data.nom,
        bibliotheque_id: data.bibliotheque_id,
        description: data.description || '',
        position_x: parseInt(data.position_x) || 0,
        position_y: parseInt(data.position_y) || 0
      };
      // Retourner les données originales pour l'invalidation du cache
      return rayonnagesAPI.update(id, cleanData).then(response => ({
        ...response,
        bibliotheque_id: data.bibliotheque_id
      }));
    },
    {
      onSuccess: (response, variables) => {
        toast.success(t('modifierRayonnagePage.updateSuccess'));
        // Récupérer le bibliotheque_id depuis response (ajouté dans la mutation) ou depuis variables ou depuis le formulaire
        const bibliothequeId = response?.bibliotheque_id || variables?.bibliotheque_id || getValues('bibliotheque_id') || rayonnage?.bibliotheque_id;
        const oldBibliothequeId = rayonnage?.bibliotheque_id;
        
        queryClient.invalidateQueries(['rayonnage', id]);
        queryClient.invalidateQueries('rayonnages');
        queryClient.invalidateQueries('rayonnages-list');
        
        // Invalider les données de la bibliothèque pour mettre à jour le nombre de rayonnages
        // Convertir en string car useParams() retourne une string dans BibliothequeDetailPage
        if (bibliothequeId) {
          const bibliothequeIdStr = String(bibliothequeId);
          // Invalider avec le pattern exact utilisé dans BibliothequeDetailPage
          queryClient.invalidateQueries(['bibliotheque', bibliothequeIdStr]);
          queryClient.invalidateQueries(['bibliotheque-rayonnages', bibliothequeIdStr]);
        }
        
        // Si le rayonnage a changé de bibliothèque, invalider aussi l'ancienne bibliothèque
        if (oldBibliothequeId && String(oldBibliothequeId) !== String(bibliothequeId)) {
          const oldBibliothequeIdStr = String(oldBibliothequeId);
          queryClient.invalidateQueries(['bibliotheque', oldBibliothequeIdStr]);
          queryClient.invalidateQueries(['bibliotheque-rayonnages', oldBibliothequeIdStr]);
        }
        
        // Invalider aussi toutes les requêtes de bibliothèques pour être sûr
        queryClient.invalidateQueries('bibliotheques');
        
        navigate(-1);
      },
      onError: (error) => {
        console.error('Erreur lors de la modification:', error);
        
        if (error.response?.status === 409) {
          toast.error(t('modifierRayonnagePage.alreadyExists'));
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          errors.forEach(err => {
            if (err.message) {
              toast.error(err.message);
            }
          });
        } else {
          toast.error(t('modifierRayonnagePage.updateError'));
        }
      }
    }
  );

  const onSubmit = (data) => {
    if (isEditMode) {
      // Passer les données avec le bibliotheque_id pour s'assurer qu'il est disponible dans onSuccess
      updateRayonnageMutation.mutate({
        ...data,
        bibliotheque_id: data.bibliotheque_id
      });
    } else {
      createRayonnageMutation.mutate(data);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const isLoadingMutation = createRayonnageMutation.isLoading || updateRayonnageMutation.isLoading;

  if (isEditMode && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isEditMode && error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('modifierRayonnagePage.loadingError', { message: error.message })}
        </Alert>
      </Box>
    );
  }

  if (isEditMode && !rayonnage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('modifierRayonnagePage.notFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header avec navigation */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{ 
                flexGrow: 1, 
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 500,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {isEditMode ? t('modifierRayonnagePage.editTitle') : t('modifierRayonnagePage.addTitle')}
            </Typography>
            <Button
              type="submit"
              variant="contained"
              startIcon={isLoadingMutation ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={isLoadingMutation}
              sx={{ 
                ml: 2,
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {isLoadingMutation ? t('modifierRayonnagePage.saving') : t('modifierRayonnagePage.save')}
            </Button>
          </Box>
        </Box>

        {/* Formulaire */}
        
        {/* Section Nom */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body1" 
            component="label"
            sx={{ 
              display: 'block',
              mb: 1,
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('modifierRayonnagePage.name')}
          </Typography>
          <Controller
            name="nom"
            control={control}
            rules={{ required: t('modifierRayonnagePage.nameRequired') }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                placeholder={t('modifierRayonnagePage.namePlaceholder')}
                error={!!errors.nom}
                helperText={errors.nom?.message}
                disabled={isLoadingMutation}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                  }
                }}
              />
            )}
          />
        </Box>

        {/* Section Bibliothèque */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body1" 
            component="label"
            sx={{ 
              display: 'block',
              mb: 1,
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('modifierRayonnagePage.library')}
          </Typography>
          <Controller
            name="bibliotheque_id"
            control={control}
            rules={{ required: t('modifierRayonnagePage.libraryRequired') }}
            render={({ field }) => (
              <FormControl 
                fullWidth 
                error={!!errors.bibliotheque_id}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                  }
                }}
              >
                <InputLabel>{t('modifierRayonnagePage.libraryLabel')}</InputLabel>
                <Select 
                  {...field} 
                  label={t('modifierRayonnagePage.libraryLabel')}
                  disabled={isLoadingMutation}
                >
                  {bibliotheques?.map((bibliotheque) => (
                    <MenuItem key={bibliotheque.id} value={bibliotheque.id}>
                      {bibliotheque.nom}
                      {bibliotheque.piece && ` (${bibliotheque.piece})`}
                    </MenuItem>
                  ))}
                </Select>
                {errors.bibliotheque_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.bibliotheque_id.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Box>

        {/* Section Description */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body1" 
            component="label"
            sx={{ 
              display: 'block',
              mb: 1,
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('modifierRayonnagePage.description')}
          </Typography>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                placeholder={t('modifierRayonnagePage.descriptionPlaceholder')}
                multiline
                rows={4}
                disabled={isLoadingMutation}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                  }
                }}
              />
            )}
          />
        </Box>
      </form>

      {/* Guard pour bloquer la navigation en cas de modifications non sauvegardées */}
      <UnsavedChangesGuard
        hasUnsavedChanges={hasUnsavedChanges}
        message={t('modifierRayonnagePage.unsavedChangesMessage')}
      />
    </Box>
  );
}

export default ModifierRayonnagePage;

